import { useState, type ReactNode } from 'react';
import { nextSort, parseSort, sortRows, type SortColumns, type SortState } from '../sort';

export interface TableSort<T> {
  /** the rows in the chosen order (the list's own order while none is chosen) */
  readonly apply: (rows: readonly T[]) => readonly T[];
  /** a column title that orders the rows by its column */
  readonly th: (key: string, className?: string) => ReactNode;
  /** "Sırala" for a phone, where a stacked table shows no titles */
  readonly picker: ReactNode;
  /** a column's order is chosen: the list's own order (and what a screen says about it) is off */
  readonly sorted: boolean;
}

const storageKey = (id: string) => `wms.sort.${id}`;

function readSaved(id: string): string | null {
  try {
    return localStorage.getItem(storageKey(id));
  } catch {
    return null;
  }
}

function save(id: string, state: SortState | null) {
  try {
    if (state) localStorage.setItem(storageKey(id), `${state.key}:${state.dir}`);
    else localStorage.removeItem(storageKey(id));
  } catch {
    // a private window: the order lasts until the page closes
  }
}

/**
 * Click-to-sort for a list table; `id` names the table, so each computer keeps its own choice.
 * Columns not in `columns` keep a plain title.
 */
export function useSort<T>(id: string, columns: SortColumns<T>): TableSort<T> {
  const [chosen, setChosen] = useState<SortState | null>(() => parseSort(readSaved(id), Object.keys(columns)));
  // a column the table no longer shows (a seller's view has no Satıcı) orders nothing
  const state = chosen && columns[chosen.key] ? chosen : null;
  const choose = (next: SortState | null) => {
    setChosen(next);
    save(id, next);
  };

  const apply = (rows: readonly T[]) => (state ? sortRows(rows, columns[state.key]!.value, state.dir) : rows);

  const th = (key: string, className?: string) => {
    const column = columns[key];
    if (!column) return null;
    const dir = state?.key === key ? state.dir : null;
    return (
      <th key={key} className={className} aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}>
        <button
          type="button"
          className={`th-sort${dir ? ' on' : ''}`}
          onClick={() => choose(nextSort(state, key, column.desc))}
          title={dir ? 'Sırayı değiştir' : `${column.label} sütununa göre sırala`}
        >
          {column.label}
          <span className="th-arrow" aria-hidden="true">
            {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '↕'}
          </span>
        </button>
      </th>
    );
  };

  const picker = (
    <label className="sort-sm">
      <span>Sırala</span>
      <select
        className="select"
        value={state ? `${state.key}:${state.dir}` : ''}
        onChange={(e) => choose(parseSort(e.target.value, Object.keys(columns)))}
      >
        <option value="">Varsayılan</option>
        {Object.entries(columns).flatMap(([key, c]) => [
          <option key={`${key}:asc`} value={`${key}:${c.desc ? 'desc' : 'asc'}`}>
            {c.label} {c.desc ? '▼' : '▲'}
          </option>,
          <option key={`${key}:desc`} value={`${key}:${c.desc ? 'asc' : 'desc'}`}>
            {c.label} {c.desc ? '▲' : '▼'}
          </option>,
        ])}
      </select>
    </label>
  );

  return { apply, th, picker, sorted: state !== null };
}
